import { useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, Loader2, DollarSign, Clock, CheckCircle } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { CustoVeiculoForm } from './CustoVeiculoForm';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { formatCurrency, formatDate } from '../../utils/format';
import { useApiEffect } from '../../hooks';
import type { CustoVeiculo, CustoVeiculoCreate, CustoVeiculoResumo, TipoCusto } from '../../types';

interface CustosVeiculoModalProps {
  isOpen: boolean;
  onClose: () => void;
  veiculoId: number;
  veiculoNome: string;
}

const TIPO_LABELS: Record<TipoCusto, string> = {
  LAUDO: 'Laudo/Vistoria',
  PNEUS: 'Pneus',
  COMBUSTIVEL: 'Combustível',
  OFICINA: 'Oficina/Mecânica',
  DOCUMENTACAO: 'Documentação',
  ESTETICA: 'Estética/Lavagem',
  OUTROS: 'Outros',
};

const TIPO_COLORS: Record<TipoCusto, string> = {
  LAUDO: 'bg-purple-100 text-purple-700',
  PNEUS: 'bg-gray-100 text-gray-700',
  COMBUSTIVEL: 'bg-yellow-100 text-yellow-700',
  OFICINA: 'bg-red-100 text-red-700',
  DOCUMENTACAO: 'bg-blue-100 text-blue-700',
  ESTETICA: 'bg-green-100 text-green-700',
  OUTROS: 'bg-orange-100 text-orange-700',
};

export function CustosVeiculoModal({ isOpen, onClose, veiculoId, veiculoNome }: CustosVeiculoModalProps) {
  const { showSuccess, showApiError } = useNotification();

  const [custos, setCustos] = useState<CustoVeiculo[]>([]);
  const [resumo, setResumo] = useState<CustoVeiculoResumo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCusto, setEditingCusto] = useState<CustoVeiculo | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadData = useCallback(async (signal: AbortSignal) => {
    setIsLoading(true);
    try {
      const [custosRes, resumoRes] = await Promise.all([
        api.get<CustoVeiculo[]>(`/veiculos/${veiculoId}/custos`, { signal }),
        api.get<CustoVeiculoResumo>(`/veiculos/${veiculoId}/custos/resumo`, { signal }),
      ]);
      setCustos(custosRes.data);
      setResumo(resumoRes.data);
    } finally {
      setIsLoading(false);
    }
  }, [veiculoId]);

  const { refetch } = useApiEffect(loadData, [veiculoId], { enabled: isOpen && !!veiculoId });

  const handleCreate = async (data: CustoVeiculoCreate) => {
    setIsSubmitting(true);
    try {
      await api.post(`/veiculos/${veiculoId}/custos`, data);
      showSuccess('Custo adicionado com sucesso');
      setShowForm(false);
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (data: CustoVeiculoCreate) => {
    if (!editingCusto) return;
    setIsSubmitting(true);
    try {
      await api.put(`/veiculos/${veiculoId}/custos/${editingCusto.id}`, data);
      showSuccess('Custo atualizado com sucesso');
      setEditingCusto(null);
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setIsDeleting(true);
    try {
      await api.delete(`/veiculos/${veiculoId}/custos/${deleteId}`);
      showSuccess('Custo excluído com sucesso');
      setDeleteId(null);
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCusto(null);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={`Custos - ${veiculoNome}`} size="xl">
        {/* Resumo */}
        {resumo && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-sm">Total</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(resumo.totalCustos)}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Pago</span>
              </div>
              <p className="text-lg font-semibold text-green-700">{formatCurrency(resumo.totalPago)}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-yellow-600 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm">Pendente</span>
              </div>
              <p className="text-lg font-semibold text-yellow-700">{formatCurrency(resumo.totalPendente)}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-600 mb-1">
                <span className="text-sm">Lançamentos</span>
              </div>
              <p className="text-lg font-semibold text-blue-700">{resumo.quantidadeLancamentos}</p>
            </div>
          </div>
        )}

        {/* Form ou Lista */}
        {showForm || editingCusto ? (
          <CustoVeiculoForm
            custo={editingCusto}
            onSubmit={editingCusto ? handleUpdate : handleCreate}
            onCancel={handleCancel}
            isLoading={isSubmitting}
          />
        ) : (
          <>
            {/* Botão Adicionar */}
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Adicionar Custo
              </button>
            </div>

            {/* Lista */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : custos.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Nenhum custo registrado para este veículo</p>
                <p className="text-sm mt-1">Clique em "Adicionar Custo" para começar</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {custos.map((custo) => (
                  <div
                    key={custo.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${TIPO_COLORS[custo.tipo]}`}>
                          {TIPO_LABELS[custo.tipo]}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          custo.status === 'PAGO' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {custo.status === 'PAGO' ? 'Pago' : 'Pendente'}
                        </span>
                      </div>
                      <p className="font-medium text-gray-900">{custo.descricao}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                        <span>{formatDate(custo.dataCusto)}</span>
                        {custo.fornecedor && <span>• {custo.fornecedor}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-semibold text-gray-900">{formatCurrency(custo.valor)}</span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setEditingCusto(custo)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteId(custo.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        type="danger"
        title="Excluir Custo"
        message="Tem certeza que deseja excluir este custo? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        isLoading={isDeleting}
      />
    </>
  );
}
